import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ArrowRightLeft, Plus, Search, X } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { COURSE_TYPE_LABEL, normalize } from "../lib/courseMeta";
const KIND_STYLE = {
  concept: "text-orange-300 bg-orange-500/10 border-orange-500/30",
  article: "text-violet-300 bg-violet-500/10 border-violet-500/30",
  vocabulary: "text-sky-300 bg-sky-500/10 border-sky-500/30",
};
const Kind = ({ c }) => (
  <span className={`shrink-0 text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded border ${KIND_STYLE[c.category] || "text-zinc-400 border-white/10"}`}>
    {COURSE_TYPE_LABEL[c.category] || c.category}
  </span>
);
/**
 * Contenus liés (éditeur admin) : value = [{ id, recip }].
 * recip = true (⇆) : le lien s'affiche aussi sur l'autre contenu ; false (→) : seulement sur ce cours.
 * Affiche aussi, en lecture seule, les contenus qui pointent vers ce cours avec un lien réciproque.
 */
export default function CourseLinksPicker({ value = [], onChange, courseId }) {
  const [q, setQ] = useState("");
  const { data: all = [] } = useQuery({
    queryKey: ["admin-courses"],
    queryFn: async () => (await api.get("/courses")).data,
  });
  const byId = useMemo(() => Object.fromEntries(all.map((c) => [c.id, c])), [all]);
  const pool = all.filter((c) => ["concept", "article", "vocabulary"].includes(c.category));
  const taken = new Set(value.map((l) => l.id));
  const results = useMemo(() => {
    const n = normalize(q.trim());
    if (!n) return [];
    return pool.filter((c) => c.id !== courseId && !taken.has(c.id) && normalize(c.title).includes(n)).slice(0, 6);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, pool, value, courseId]);
  const incoming = courseId
    ? pool.filter((c) => c.id !== courseId && !taken.has(c.id) && (c.links || []).some((l) => l.id === courseId && l.recip))
    : [];
  const set = (next) => onChange(next);
  return (
    <div className="space-y-3" data-testid="course-links-picker">
      {value.length === 0 ? (
        <p className="text-xs text-zinc-500">Aucun lien pour l'instant.</p>
      ) : (
        <div className="space-y-1.5">
          {value.map((l) => {
            const c = byId[l.id];
            if (!c) return null;
            return (
              <div key={l.id} className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1.5" data-testid={`course-link-${l.id}`}>
                <Kind c={c} />
                <span className="flex-1 min-w-0 truncate text-sm text-zinc-200">{c.title}</span>
                <button
                  type="button"
                  onClick={() => set(value.map((x) => (x.id === l.id ? { ...x, recip: !x.recip } : x)))}
                  title={l.recip ? "Réciproque : le lien s'affiche aussi sur l'autre contenu. Cliquer pour le limiter à ce cours." : "Sens unique : le lien ne s'affiche que sur ce cours. Cliquer pour le rendre réciproque."}
                  className={`p-1 rounded ${l.recip ? "text-emerald-300 bg-emerald-500/10" : "text-zinc-500 hover:text-zinc-300"}`}
                  data-testid={`course-link-recip-${l.id}`}
                >
                  {l.recip ? <ArrowRightLeft className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
                </button>
                <button type="button" onClick={() => set(value.filter((x) => x.id !== l.id))} className="p-1 text-zinc-500 hover:text-rose-300" aria-label="Retirer">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ajouter un cours, une rétrospective, une définition…"
          className="pl-8 bg-white/[0.03] border-white/10"
          data-testid="course-links-search"
        />
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-md border border-white/10 bg-card shadow-xl overflow-hidden">
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { set([...value, { id: c.id, recip: true }]); setQ(""); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-white/5"
              >
                <Plus className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="truncate flex-1">{c.title}</span>
                <Kind c={c} />
              </button>
            ))}
          </div>
        )}
      </div>
      <p className="text-[11px] text-zinc-500 leading-relaxed">
        <ArrowRightLeft className="inline w-3 h-3 -mt-0.5 text-emerald-300" /> réciproque : le lien s'affiche aussi sur l'autre contenu.{" "}
        <ArrowRight className="inline w-3 h-3 -mt-0.5" /> sens unique : seulement sur ce cours.
      </p>
      {incoming.length > 0 && (
        <div className="pt-3 border-t border-white/5">
          <div className="text-[11px] text-zinc-500 mb-1.5">Affichés aussi ici, car liés depuis un autre contenu :</div>
          {incoming.map((c) => (
            <div key={c.id} className="flex items-center gap-2 py-1 text-sm text-zinc-400">
              <ArrowRightLeft className="w-3.5 h-3.5 text-emerald-300/70 shrink-0" />
              <span className="truncate">{c.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
